import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { studentService } from '../../services/api';
import { useTheme } from '../../context/ThemeContext';

const ParentAttendance: React.FC = () => {
  const { isDarkMode } = useTheme();
  const [attendance, setAttendance] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState<string>('');

  useEffect(() => {
    fetchStudents();
  }, []);

  useEffect(() => {
    if (selectedStudent) {
      fetchAttendance();
    }
  }, [selectedStudent]);

  const fetchStudents = async () => {
    try {
      const response = await studentService.getAll();
      const parentStudents = response.data.students || [];
      setStudents(parentStudents);
      if (parentStudents.length > 0) {
        setSelectedStudent(parentStudents[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch students:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendance = async () => {
    try {
      const response = await studentService.getAll();
      const studentAttendance = response.data.students?.find((s: any) => s.id === selectedStudent)?.attendance || [];
      setAttendance(studentAttendance);
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
    }
  };

  if (loading) {
    return <Layout title="Children's Attendance"><div className="text-center py-8">Loading...</div></Layout>;
  }

  const presentDays = attendance.filter((a: any) => a.status === 'present').length;
  const absentDays = attendance.filter((a: any) => a.status === 'absent').length;
  const lateDays = attendance.filter((a: any) => a.status === 'late').length;
  const attendancePercentage = attendance.length > 0 ? ((presentDays / attendance.length) * 100).toFixed(2) : 0;

  return (
    <Layout title="Children's Attendance">
      <div className="mb-6">
        <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Select Child</label>
        <select
          value={selectedStudent}
          onChange={(e) => setSelectedStudent(e.target.value)}
          className={`w-full md:w-64 border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
        >
          {students.map((student) => (
            <option key={student.id} value={student.id}>
              {student.user?.firstName} {student.user?.lastName} (Class {student.class?.name})
            </option>
          ))}
        </select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-4`}>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Attendance %</p>
          <p className="text-2xl font-bold text-green-600">{attendancePercentage}%</p>
        </div>
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-4`}>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Present</p>
          <p className="text-2xl font-bold text-blue-600">{presentDays}</p>
        </div>
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-4`}>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Absent</p>
          <p className="text-2xl font-bold text-red-600">{absentDays}</p>
        </div>
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-4`}>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Late</p>
          <p className="text-2xl font-bold text-yellow-600">{lateDays}</p>
        </div>
      </div>

      {/* Attendance Table */}
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow overflow-hidden`}>
        <table className="w-full">
          <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}>
            <tr>
              <th className={`text-left px-6 py-3 text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Date</th>
              <th className={`text-left px-6 py-3 text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status</th>
              <th className={`text-left px-6 py-3 text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {attendance.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center py-8 text-gray-500">No attendance records found</td>
              </tr>
            ) : (
              attendance.map((record, index) => (
                <tr key={index} className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <td className={`px-6 py-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {record.date ? new Date(record.date).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className={`px-6 py-4`}>
                    <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                      record.status === 'present' ? 'bg-green-100 text-green-800' :
                      record.status === 'absent' ? 'bg-red-100 text-red-800' :
                      record.status === 'late' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {record.status || 'N/A'}
                    </span>
                  </td>
                  <td className={`px-6 py-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{record.remarks || '-'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
};

export default ParentAttendance;
